import { LEGAL_VERSIONS } from '../constants/version';
import {
  LegalDocument,
  LegalDocumentType,
  LegalParagraph,
  LegalSection,
} from '../types/legalDocument';

export const subscriptionPolicy: LegalDocument = {
  id: 'subscription-policy',

  title: 'POLÍTICA DE ASSINATURAS DA NEXORA',

  version: LEGAL_VERSIONS.subscriptions,

  source: 'docs/legal/subscription-policy.md',

  documentType: 'subscription' as LegalDocumentType,

  lastUpdated: 'Julho de 2026',

  effectiveDate: 'Julho de 2026',

  sections: [
    {
      id: 'commitment',

      title: 'Compromisso da Nexora',

      paragraphs: [
        {
          id: 'commitment-1',
          text: 'A Nexora busca oferecer uma experiência transparente em relação ao acesso à plataforma, seus planos e suas assinaturas.',
        },

        {
          id: 'commitment-2',
          text: 'Esta Política descreve como funciona atualmente a contratação, a ativação, a renovação, a expiração e as situações que podem impactar a disponibilidade dos serviços.',
        },

        {
          id: 'commitment-3',
          text: 'Nosso objetivo é garantir previsibilidade, clareza e segurança para todos os usuários, preservando uma estrutura preparada para a evolução futura da plataforma.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'platform-access',

      title: '1. Acesso à Plataforma',

      paragraphs: [
        {
          id: 'platform-access-1',
          text: 'Atualmente, o acesso aos recursos da Nexora poderá depender da contratação ou liberação administrativa de um plano de assinatura.',
        },

        {
          id: 'platform-access-2',
          text: 'A criação de uma conta não garante automaticamente o acesso aos recursos restritos da plataforma.',
        },

        {
          id: 'platform-access-3',
          text: 'Após o cadastro, a conta poderá permanecer em análise administrativa até sua aprovação, conforme os critérios internos vigentes.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'available-plans',

      title: '2. Planos Disponíveis',

      paragraphs: [
        {
          id: 'available-plans-1',
          text: 'A Nexora poderá disponibilizar diferentes modalidades de assinatura conforme sua estratégia comercial, operacional e técnica.',
        },

        {
          id: 'available-plans-2',
          text: 'Atualmente, as modalidades disponíveis serão aquelas informadas oficialmente pela Nexora no momento da contratação, liberação administrativa ou renovação do acesso.',
        },

        {
          id: 'available-plans-3',
          text: 'A disponibilidade, ativação e continuidade de cada modalidade dependerão das condições vigentes no momento da contratação ou liberação do acesso.',
        },

        {
          id: 'available-plans-4',
          text: 'Novas modalidades poderão ser disponibilizadas futuramente, conforme evolução da plataforma, mediante atualização desta Política ou comunicação oficial da Nexora.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'subscription-activation',

      title: '3. Ativação da Assinatura',

      paragraphs: [
        {
          id: 'subscription-activation-1',
          text: 'A assinatura será considerada ativa após confirmação administrativa ou financeira, conforme a modalidade vigente e os processos atualmente disponíveis na plataforma.',
        },

        {
          id: 'subscription-activation-2',
          text: 'Enquanto a assinatura não estiver ativa, determinados recursos poderão permanecer indisponíveis.',
        },

        {
          id: 'subscription-activation-3',
          text: 'A ativação poderá depender de validações internas, confirmação de pagamento, aprovação administrativa ou outros procedimentos compatíveis com o modelo contratado.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'renewal',

      title: '4. Renovação',

      paragraphs: [
        {
          id: 'renewal-1',
          text: 'A renovação poderá ocorrer conforme a modalidade contratada e os recursos disponíveis no momento.',
        },

        {
          id: 'renewal-2',
          text: 'Atualmente, a renovação poderá ocorrer:',
        },

        {
          id: 'renewal-3',
          text: '* manualmente, mediante confirmação da equipe administrativa;',
        },

        {
          id: 'renewal-4',
          text: '* automaticamente, quando houver integração com meios de pagamento compatíveis;',
        },

        {
          id: 'renewal-5',
          text: '* conforme regras específicas associadas ao plano contratado.',
        },

        {
          id: 'renewal-6',
          text: 'A forma de renovação poderá variar de acordo com o plano, o ciclo vigente, a disponibilidade técnica e as condições comerciais aplicáveis.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'subscription-expiration',

      title: '5. Expiração da Assinatura',

      paragraphs: [
        {
          id: 'subscription-expiration-1',
          text: 'Ao término do período contratado, a assinatura poderá expirar caso não seja renovada ou reativada.',
        },

        {
          id: 'subscription-expiration-2',
          text: 'Quando isso ocorrer, o acesso aos recursos restritos da plataforma poderá ser temporariamente suspenso até a regularização da assinatura.',
        },

        {
          id: 'subscription-expiration-3',
          text: 'A expiração da assinatura não implica, por si só, exclusão da conta do usuário ou remoção automática dos registros administrativos associados.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'expiration-suspension',

      title: '6. Suspensão por Expiração',

      paragraphs: [
        {
          id: 'expiration-suspension-1',
          text: 'Uma assinatura vencida poderá resultar na suspensão temporária do acesso aos recursos restritos da plataforma.',
        },

        {
          id: 'expiration-suspension-2',
          text: 'Durante esse período:',
        },

        {
          id: 'expiration-suspension-3',
          text: '* os dados da conta permanecerão preservados, conforme as políticas aplicáveis;',
        },

        {
          id: 'expiration-suspension-4',
          text: '* o histórico administrativo será mantido;',
        },

        {
          id: 'expiration-suspension-5',
          text: '* a conta poderá ser reativada após a renovação, regularização ou nova liberação do acesso.',
        },

        {
          id: 'expiration-suspension-6',
          text: 'Essa medida busca proteger a integridade da plataforma e manter a continuidade dos registros do usuário.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'reactivation',

      title: '7. Reativação',

      paragraphs: [
        {
          id: 'reactivation-1',
          text: 'Após a confirmação da renovação, regularização ou nova liberação administrativa, a conta poderá ser reativada pela administração da Nexora ou por processos automáticos quando disponibilizados.',
        },

        {
          id: 'reactivation-2',
          text: 'Sempre que possível, a reativação ocorrerá preservando o histórico da conta e respeitando as condições do plano vigente.',
        },

        {
          id: 'reactivation-3',
          text: 'A reativação poderá depender da modalidade contratada, da situação administrativa da conta e da disponibilidade dos recursos aplicáveis.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'cancellation',

      title: '8. Cancelamento',

      paragraphs: [
        {
          id: 'cancellation-1',
          text: 'O usuário poderá solicitar o cancelamento da assinatura conforme os canais oficiais disponibilizados pela Nexora.',
        },

        {
          id: 'cancellation-2',
          text: 'O cancelamento poderá estar sujeito às condições do plano contratado, ao ciclo vigente e às regras comerciais aplicáveis no momento da solicitação.',
        },

        {
          id: 'cancellation-3',
          text: 'O cancelamento não implica, automaticamente, a exclusão definitiva da conta ou dos registros administrativos.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'plan-changes',

      title: '9. Alterações de Planos',

      paragraphs: [
        {
          id: 'plan-changes-1',
          text: 'A Nexora poderá criar, modificar, substituir ou descontinuar planos de assinatura para acompanhar a evolução da plataforma, mudanças comerciais, necessidades operacionais ou aprimoramentos técnicos.',
        },

        {
          id: 'plan-changes-2',
          text: 'Sempre que possível, mudanças relevantes serão comunicadas previamente aos usuários afetados.',
        },

        {
          id: 'plan-changes-3',
          text: 'Novas modalidades de assinatura poderão ser incorporadas mediante atualização desta Política, comunicação oficial ou disponibilização pela plataforma.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'refunds',

      title: '10. Reembolsos',

      paragraphs: [
        {
          id: 'refunds-1',
          text: 'As condições para reembolso poderão variar conforme:',
        },

        {
          id: 'refunds-2',
          text: '* o plano contratado;',
        },

        {
          id: 'refunds-3',
          text: '* a forma de pagamento utilizada;',
        },

        {
          id: 'refunds-4',
          text: '* o ciclo de assinatura vigente;',
        },

        {
          id: 'refunds-5',
          text: '* a legislação aplicável;',
        },

        {
          id: 'refunds-6',
          text: '* as condições comerciais informadas no momento da contratação.',
        },

        {
          id: 'refunds-7',
          text: 'Quando houver direito ao reembolso, ele será analisado conforme as circunstâncias específicas de cada solicitação e os critérios aplicáveis à modalidade contratada.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'policy-updates',

      title: '11. Atualizações desta Política',

      paragraphs: [
        {
          id: 'policy-updates-1',
          text: 'Esta Política poderá ser revisada periodicamente para refletir alterações legais, comerciais, operacionais ou técnicas.',
        },

        {
          id: 'policy-updates-2',
          text: 'Sempre que necessário, uma nova versão será publicada e poderá ser solicitada uma nova concordância dos usuários.',
        },

        {
          id: 'policy-updates-3',
          text: 'A continuidade da utilização da plataforma poderá depender da aceitação da versão vigente desta Política, quando aplicável.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'contact',

      title: '12. Contato',

      paragraphs: [
        {
          id: 'contact-1',
          text: 'Em caso de dúvidas relacionadas às assinaturas, renovações, cancelamentos ou planos disponíveis, o usuário poderá entrar em contato pelos canais oficiais de atendimento da Nexora.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'final-considerations',

      title: 'Considerações Finais',

      paragraphs: [
        {
          id: 'final-considerations-1',
          text: 'A Nexora acredita que uma relação transparente é essencial para construir confiança.',
        },

        {
          id: 'final-considerations-2',
          text: 'Por isso, buscamos apresentar informações claras sobre nossos planos e assinaturas, preservando a segurança, a previsibilidade e a continuidade da experiência dos usuários durante todo o ciclo de utilização da plataforma.',
        },

        {
          id: 'final-considerations-3',
          text: 'Esta Política foi estruturada para refletir o funcionamento atual da Nexora e permitir sua evolução responsável conforme novas modalidades, recursos ou processos sejam disponibilizados.',
        },
      ] satisfies LegalParagraph[],
    },
  ] satisfies LegalSection[],
};
